/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useAuthContext } from '../contexts/AuthContext';
import { MenuItem, Select, SelectChangeEvent, Checkbox, FormControlLabel } from '@mui/material';
import { SnackbarProvider } from 'notistack';
import { Fragment, useState } from 'react';
import { useLoanContext } from '../contexts/LoanContext';
import EnvelopeWithBox from './../components/EnvelopeWithBox';
import LoanHistoryHistoryForm from './../components/forms/LoanHistoryHistoryForm';
import LoanHistoryTransactionForm from './../components/forms/LoanHistoryTransactionForm';
import Style from './../css/LoanHistory.module.css';
import { LoanHistoryProvider } from '../contexts/LoanHistoryTransactionContext';

enum Sorted {
  DESC_ORDER = 'Descending Order (Most Recent First)',
  ASC_ORDER = 'Ascending Order (Oldest First)',
  PAYEE = 'Payee',
}

enum Tables {
  TRANSACTION = 'All Transactions',
  PAYMENT = 'Payments Only',
  ESCROW = 'All Escrow Disbursements',
  TAX = 'Tax Disbursements Only',
  HAZARD = 'Hazard Insurance Disbursements Only',
  MIP = 'MIP Insurance Disbursements Only',
  OTHER = 'Other Disbursements Only',
  OTP = 'One Time Payments',
  FILE_MAINT = 'File Maintenance',
}

export { Sorted, Tables };

export default function LoanHistoryPage() {
  const { loanNumber } = useLoanContext();
  const [selectedTable, setSelectedTable] = useState(Tables.TRANSACTION as string);
  const [selectedSort, setSelectedSort] = useState(Sorted.DESC_ORDER as string);
  const [showFullMigrationHistory, setShowFullMigrationHistory] = useState(false);
  const { servicerUser } = useAuthContext();

  const handleChangeTable = (event: SelectChangeEvent) => {
    setSelectedTable(event.target.value);
  };

  const handleChangeSort = (event: SelectChangeEvent) => {
    setSelectedSort(event.target.value);
  };

  return (
    <Fragment>
      <EnvelopeWithBox blockName='Loan History'>
        <div>
          <Select
            value={selectedTable}
            label='History Type'
            onChange={handleChangeTable}
            defaultValue={''}
            className={Style.select}
          >
            {Object.values(Tables).map((value: string, index: number) => (
              <MenuItem key={index} value={value}>
                {value}
              </MenuItem>
            ))}
          </Select>
          <Select
            value={selectedSort}
            label='Sorted In'
            onChange={handleChangeSort}
            defaultValue={''}
            className={Style.select}
          >
            {Object.values(Sorted).map((value: string, index: number) => (
              <MenuItem key={index} value={value}>
                {value}
              </MenuItem>
            ))}
          </Select>
          {servicerUser && selectedTable !== Tables.FILE_MAINT && (
            <FormControlLabel
              label='Show All Transactions'
              control={
                <Checkbox
                  checked={showFullMigrationHistory}
                  onChange={(e) => {
                    setShowFullMigrationHistory(e.target.checked);
                  }}
                />
              }
            />
          )}
          <div className={Style.height}>
            <SnackbarProvider maxSnack={3} preventDuplicate anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
              {selectedTable === Tables.FILE_MAINT ? (
                <LoanHistoryHistoryForm loanNumber={loanNumber} selectedSort={selectedSort} />
              ) : (
                <LoanHistoryProvider>
                  <LoanHistoryTransactionForm
                    loanNumber={loanNumber}
                    selectedTable={selectedTable}
                    selectedSort={selectedSort}
                    showFullMigrationHistory={showFullMigrationHistory}
                  />
                </LoanHistoryProvider>
              )}
            </SnackbarProvider>
          </div>
        </div>
      </EnvelopeWithBox>
    </Fragment>
  );
}
